import { YokaiInput, YokaiOutput } from '../types/yokai';
import { generateYokaiImages } from './falApi';
import { generateYokaiData as generateOpenAIYokaiData } from './openaiService';
import { logger } from '../utils/logger';
import { validateYokaiInput } from '../utils/validation';
import { config } from '../constants/config';

/**
 * テスト用のダミーデータ生成
 */
const generateDummyYokaiData = (input: YokaiInput): { name: string; description: string; likes: string; dislikes: string; catchphrase: string; imagePrompt: string } => {
  const dummyNames = ['テスト妖怪', 'デバッグ鬼', 'モック化け物', 'ダミー精霊', 'フェイク幽霊'];
  const randomName = dummyNames[Math.floor(Math.random() * dummyNames.length)];
  
  return {
    name: randomName,
    description: `${input.habitat}に住むテスト用の妖怪。${input.appearance}の外見で${input.attribute}の属性を持つ。これはテスト用のダミーデータです。`,
    likes: 'テストケース、デバッグ作業',
    dislikes: 'プロダクション環境、課金',
    catchphrase: 'テスト中でござる〜',
    imagePrompt: `A cute test yokai creature living in ${input.habitat}, with ${input.appearance} appearance and ${input.attribute} attributes. Kawaii anime style.`
  };
};

/**
 * テスト用のダミー画像データ生成
 */
const generateDummyImages = () => {
  // テストモード時はimg/demo.jpgを使用
  const demoImageUrl = './img/demo.jpg';
  
  return [{
    url: demoImageUrl,
    width: 512,
    height: 384,
    content_type: 'image/jpeg' as const
  }];
};




/**
 * 妖怪データのみを生成する（画像なし）
 */
export const generateYokaiDataOnly = async (input: YokaiInput): Promise<{ name: string; description: string; likes: string; dislikes: string; catchphrase: string; imagePrompt: string }> => {
  // 入力データの検証
  const validatedInput = validateYokaiInput(input);
  logger.yokaiGeneration('データ生成開始', '妖怪データ生成開始', validatedInput);
  
  // テストモードの場合はダミーデータを返す
  if (config.isTestMode) {
    logger.yokaiGeneration('テストモード', 'ダミーデータ生成中...');
    // テスト用の遅延を追加（実際のAPI呼び出しをシミュレート）
    await new Promise(resolve => setTimeout(resolve, 1000));
    const dummyData = generateDummyYokaiData(validatedInput);
    logger.yokaiGeneration('テストモード', 'ダミーデータ生成完了', dummyData);
    return dummyData;
  }
  
  // OpenAI APIで妖怪データを一括生成
  logger.yokaiGeneration('AI生成', 'OpenAI APIで妖怪データ生成中...');
  const yokaiData = await generateOpenAIYokaiData(validatedInput);
  logger.yokaiGeneration('AI生成', 'OpenAI生成完了', yokaiData);
  return yokaiData;
};

/**
 * 妖怪画像のみを生成する
 */
export const generateYokaiImagesOnly = async (imagePrompt: string) => {
  try {
    logger.yokaiGeneration('画像生成', '画像生成プロンプト', { prompt: imagePrompt });
    
    // テストモードの場合はダミー画像を返す
    if (config.isTestMode) {
      logger.yokaiGeneration('テストモード', 'ダミー画像生成中...');
      // テスト用の遅延を追加（実際のAPI呼び出しをシミュレート）
      await new Promise(resolve => setTimeout(resolve, 2000));
      const dummyImages = generateDummyImages();
      logger.yokaiGeneration('テストモード', 'ダミー画像生成完了', { count: dummyImages.length });
      return dummyImages;
    }
    
    const images = await generateYokaiImages(imagePrompt);
    logger.yokaiGeneration('画像生成', '画像生成完了', { count: images.length });
    return images;
  } catch (error) {
    logger.error('YOKAI_GEN', '画像生成エラー', error);
    throw error;
  }
};

/**
 * 妖怪データを生成する（メイン関数）
 */
export const generateYokaiData = async (input: YokaiInput): Promise<YokaiOutput> => {
  logger.yokaiGeneration('全体生成', '妖怪データ生成開始', input);
  
  // 妖怪データのみ先に生成
  const yokaiData = await generateYokaiDataOnly(input);
  
  // 画像生成
  const images = await generateYokaiImagesOnly(yokaiData.imagePrompt);
  
  const result = {
    name: yokaiData.name,
    description: yokaiData.description,
    likes: yokaiData.likes,
    dislikes: yokaiData.dislikes,
    catchphrase: yokaiData.catchphrase,
    images
  };
  
  logger.yokaiGeneration('全体生成', '妖怪データ生成完了', result);
  return result;
};
